import React from 'react';
import { ResponsiveImage } from '../components/ResponsiveImage';
import { ArrowRight, Target, Users, Award, Globe, Heart, Lightbulb } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AProposPage = () => {
  const values = [
    { icon: Target, title: 'Excellence', description: 'Nous nous engageons à fournir un service de la plus haute qualité à chacun de nos clients et candidats.' },
    { icon: Users, title: 'Partenariat', description: 'Nous construisons des relations durables basées sur la confiance et la compréhension mutuelle.' },
    { icon: Heart, title: 'Intégrité', description: 'Nous agissons toujours avec honnêteté, transparence et respect envers toutes les parties.' },
    { icon: Lightbulb, title: 'Innovation', description: 'Nous adoptons les meilleures pratiques et technologies pour rester à la pointe du recrutement.' }
  ];

  const milestones = [
    { year: '1998', event: 'Fondation de Recrutement Suisse à Genève' },
    { year: '2003', event: 'Ouverture du bureau de Zurich' },
    { year: '2008', event: 'Expansion à Lausanne et Bâle' },
    { year: '2015', event: 'Lancement des services RPO' },
    { year: '2020', event: '10,000 placements réalisés' },
    { year: '2024', event: '7ème année consécutive - Meilleur cabinet de recrutement' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="relative bg-gray-900 text-white py-24">
        <div className="absolute inset-0">
          <ResponsiveImage loading="eager"
            src="https://images.unsplash.com/photo-1573649647330-b3a1375227fe?w=1600&q=80"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-serif mb-6">Notre histoire</h1>
          <p className="text-xl opacity-90 max-w-2xl">
            Depuis plus de 25 ans, Recrutement Suisse accompagne les entreprises et les talents dans leurs projets les plus ambitieux.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-serif text-gray-800 mb-6">Notre mission</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Chez Recrutement Suisse, notre mission est de créer des connexions durables entre les talents et les entreprises. Nous croyons que chaque placement réussi contribue à construire un monde professionnel plus épanouissant.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Notre approche personnalisée et notre expertise sectorielle nous permettent de comprendre en profondeur les besoins de nos clients et les aspirations de nos candidats.
              </p>
            </div>
            <div className="relative">
              <ResponsiveImage sizes="(min-width: 1280px) 576px, (min-width: 1024px) 50vw, 100vw"
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=800&q=80"
                alt="Notre équipe"
                className="rounded-lg shadow-lg"
              />
              <div className="absolute -bottom-6 left-0 sm:-left-6 bg-[#E30022] text-white p-6 rounded-lg shadow-lg">
                <p className="text-3xl font-bold">25+</p>
                <p className="text-sm">Années d'expérience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-gray-800 mb-12 text-center">Nos valeurs</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md text-center">
                <div className="w-14 h-14 bg-[#E30022]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-7 w-7 text-[#E30022]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-gray-800 mb-12 text-center">Notre parcours</h2>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gray-200"></div>
            {milestones.map((milestone, index) => (
              <div key={index} className={`relative flex items-center mb-8 ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                  <div className="bg-white rounded-lg shadow-md p-4">
                    <span className="text-[#E30022] font-bold text-lg">{milestone.year}</span>
                    <p className="text-gray-700 mt-1">{milestone.event}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-[#E30022] rounded-full border-4 border-white shadow"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#E30022] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold mb-2">10,000+</p>
              <p className="opacity-90">Placements réussis</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">500+</p>
              <p className="opacity-90">Entreprises partenaires</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">50+</p>
              <p className="opacity-90">Consultants experts</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">3</p>
              <p className="opacity-90">Bureaux en Suisse</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-gray-800 mb-4">Rejoignez notre aventure</h2>
          <p className="text-gray-600 text-lg mb-8">
            Vous souhaitez faire partie de notre équipe ou travailler avec nous ?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/carrieres"
              className="inline-flex items-center gap-2 bg-[#E30022] text-white px-8 py-4 rounded hover:bg-[#c7001d] transition-colors font-medium group"
            >
              Nous rejoindre
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="/recrutement"
              className="inline-flex items-center gap-2 border-2 border-[#E30022] text-[#E30022] px-8 py-4 rounded hover:bg-[#E30022] hover:text-white transition-colors font-medium group"
            >
              Travailler avec nous
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AProposPage;
